<script lang="ts" setup>
import { computed, ref, type PropType } from "vue";
import { v4 as uuidv4 } from "uuid";
import { AttachmentUploaderType } from "../types/attachUploader";

// Define the key for the file input
const fileInputKey = ref(uuidv4());

const emit = defineEmits([
  "update:modelValue",
  "onUploadSuccess",
  "onOverSizeLimit",
  "onErrorEmptyFile",
]);

const uploaderItem = ref<AttachmentUploaderType[]>([]);
const prop = defineProps({
  modelValue: {
    type: Array<AttachmentUploaderType>,
    default: () => [],
  },
  text: {
    type: String,
    default: "Drop files here or click to upload.",
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  fileType: {
    type: String,
    default: undefined
  },
  disabled: {
    type: Boolean,
    default: false
  },
  fullHeight: {
    type: Boolean,
    default: true,
  },
  density: {
    type: String as PropType<'default' | 'comfortable' | 'compact'>,
    default: undefined,
  },
  borderClass: {
    type: String,
    default: ''
  },
  checkSizeLimit: {
    type: Boolean,
    default: true,
  },
  sizeLimit: {
    type: Number,
    default: 5000000 //5 MB
  },
  acceptFile: {
    type: String,
    default: 'image/*,.pdf'
  }
});

const getReadingFile = async (reader: FileReader, file: any) => {
  const reading = new Promise(resolve => {
    reader.onload = e => {
      resolve({
        blob: e.target?.result,
        src: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        id: uuidv4(), // Generate a new id for every file read
        fileType: file.type
      });
    };
  });

  reader.readAsDataURL(file);
  return reading;
}

const files = computed({
  get() {
    const existingFiles: AttachmentUploaderType[] = prop.modelValue.map(
      (data: any, index) => {
        return {
          ...data,
          // blob: data && data.blob ? data.blob : data,
          src: data && data.src ? data.src : data,
          name: (data && data.name) || `uploaded-img-` + index + ".png",
          size: (data && data.size) || 1024,
          id: (data && data.id) || uuidv4(),
        };
      }
    );
    uploaderItem.value = existingFiles;
    return existingFiles;
  },
  async set(value: any) {
    const prommistData: Array<Promise<any>> = value.map(async (file: any, index: number) => {
      if (!file.id) {
        const fileReader = new FileReader();
        const readedFile = getReadingFile(fileReader, file)

        return readedFile.then(file => {
          return file;
        });
      }
      return file;
    });

    // Add new files to uploaderItem
    let data = await Promise.all(prommistData)
    if (prop.multiple) {
      uploaderItem.value = [...uploaderItem.value, ...data];
    } else {
      uploaderItem.value = data;
    }
    // Check file size limit
    if (prop.checkSizeLimit && uploaderItem.value.filter(o => parseInt(o.size) > prop.sizeLimit)?.length > 0) {
      emit("onOverSizeLimit");
      uploaderItem.value = uploaderItem.value.filter(o => parseInt(o.size) <= prop.sizeLimit);
    } else if (uploaderItem.value.filter(o => parseInt(o.size) === 0)?.length > 0) {
      emit("onErrorEmptyFile");
      uploaderItem.value = uploaderItem.value.filter(o => parseInt(o.size) != 0);
    } else {
      emit("update:modelValue", uploaderItem.value.map((item: any, index: number) => {
        return item;
      }));
      emit("onUploadSuccess");
    }
  },
});

const acceptString = computed(() => {
  if (prop.acceptFile.includes('video')) {
    return 'IMAGE, VIDEO OR PDF'
  }
  return 'IMG, JPG, PNG OR PDF';
});

const removeFile = (removeIndex: number) => {
  uploaderItem.value = uploaderItem.value.filter(
    (uploaded: any, index: number) => index !== removeIndex
  );

  // Update modelValue
  emit("update:modelValue", uploaderItem.value.map((item: any) => item));

  // Change the key to force re-render of the file input
  fileInputKey.value = uuidv4();
};

defineExpose({
  removeFile
});
</script>
<template>
  <div class="position-relative w-100" :class="{ 'h-100': fullHeight }">
    <v-file-input v-model="files" :multiple="multiple" variant="outlined" :clearable="false" prepend-icon=""
      :key="fileInputKey" :class="['attach-uploader', borderClass, { 'h-100': fullHeight }]"
      :density="density" :accept="acceptFile" hide-details :disabled="disabled" />
    <div class="attach-uploader-content w-100 d-flex align-center justify-center px-2">
      <div class="attach-icon border px-1 py-1 mr-3" :class="disabled ? 'text-disabled' : ''">
        <v-icon icon="mdi mdi-cloud-upload-outline" size="20" :class="disabled ? 'text-disabled' : ''"></v-icon>
      </div>
      <div class="attach-text h-100 d-flex flex-column align-center justify-center">
        <div>
          <strong class="mr-1" :class="disabled ? 'text-disabled' : ''">{{ $t('t-default-upload-text') }}</strong>
          <span :class="disabled ? 'text-disabled' : ''">{{ $t('t-default-drop-file-text') }}</span>
        </div>
        <div class="d-flex justify-center">
          <span :class="disabled ? 'text-disabled' : ''">{{ acceptString }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
